import type { User } from "firebase/auth";
import { createContext, createElement, type ReactNode, useContext, useEffect, useMemo, useState } from "react";
import * as api from "./api";

/** The signed-in member that a level dot asks the cone for; null outside the workspace. */
export const ConeContext = createContext<{ user: User; slug: string } | null>(null);

export function ConeMember({ user, slug, children }: { user: User; slug: string; children: ReactNode }) {
  const value = useMemo(() => ({ user, slug }), [user, slug]);
  return createElement(ConeContext.Provider, { value }, children);
}

/** "none": no estimate (empty days, or a 404 "no estimate" from an older API). "failed": any other error. */
export type ConeLoad = api.FillCone | "none" | "failed" | "loading";

const CACHE_MS = 5 * 60_000;
const cache = new Map<string, { at: number; found: api.FillCone | "none" | "failed" }>();

export const noEstimate = (e: unknown) => e instanceof api.ApiError && e.status === 404 && e.detail === "no estimate";

/** The cone of one account, fetched when `open` turns true, kept for a few minutes, and aborted on close. */
export function useCone(accountId: string | null | undefined, open: boolean, past?: number): ConeLoad | null {
  const member = useContext(ConeContext);
  const key = member && accountId ? `${member.slug}/${accountId}${past ? `?past=${past}` : ""}` : null;
  const [found, setFound] = useState<ConeLoad>("loading");
  useEffect(() => {
    if (!open || !key || !member || !accountId) return;
    const kept = cache.get(key);
    if (kept && Date.now() - kept.at < CACHE_MS) {
      setFound(kept.found);
      return;
    }
    setFound("loading");
    const stop = new AbortController();
    const started = performance.now();
    member.user
      .getIdToken()
      .then((token) => api.fillCone(token, accountId, stop.signal, past))
      .then(
        (cone) => {
          const result = cone.days.length ? cone : "none";
          cache.set(key, { at: Date.now(), found: result });
          const points = cone.history?.length ?? 0;
          console.info(`fill cone: account=${accountId} past=${cone.past ?? "old-api"} days=${cone.days.length} points=${points} ms=${Math.round(performance.now() - started)}`);
          setFound(result);
        },
        (e: unknown) => {
          if (stop.signal.aborted) return;
          const result = noEstimate(e) ? "none" : "failed";
          cache.set(key, { at: Date.now(), found: result });
          const status = e instanceof api.ApiError ? e.status : "network";
          console.info(`fill cone: account=${accountId} status=${status} ${result === "none" ? "no estimate" : "no card"}`);
          setFound(result);
        },
      );
    return () => stop.abort();
  }, [open, key]);
  return key ? found : null;
}
